import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { BuyerProfitability, FpaService } from '../../../services/fpa.service';
import { firstOfYearLocalIso, toLocalIsoDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/format';

const pct = (v: number | null) => (v === null || v === undefined ? 'n/a' : `${v.toFixed(2)}%`);

const BuyerProfitabilityPage: React.FC = () => {
    const toast = useToast();
    const [from, setFrom] = useState(firstOfYearLocalIso());
    const [to, setTo] = useState(toLocalIsoDate());
    const [data, setData] = useState<BuyerProfitability | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try { setLoading(true); setData(await FpaService.getBuyerProfitability(from, to)); }
        catch (err) { toast.showApiError(err, 'Failed to load buyer profitability'); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showPlan = !!data && data.rows.some(r => r.planRevenue !== null);

    return (
        <Box data-help-page="accounting.buyerProfit.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Buyer-wise Profitability</Typography>
            <Box data-help-section="accounting.buyerProfit.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.buyerProfit.from" label="Ship from" type="date" size="small" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <TextField data-help="accounting.buyerProfit.to" label="Ship to" type="date" size="small" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <Button data-help="accounting.buyerProfit.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
            </Box>
            {data && (
                <>
                    {data.notes.map(n => <Alert key={n} severity="info" sx={{ mb: 1 }}>{n}</Alert>)}
                    <TableContainer component={Paper} variant="outlined">
                        <Table data-help-section="accounting.buyerProfit.table" size="small" sx={{ minWidth: 760 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="accounting.buyerProfit.col.buyer">Buyer</TableCell><TableCell data-help="accounting.buyerProfit.col.orders" align="right">Orders (costed)</TableCell><TableCell data-help="accounting.buyerProfit.col.revenue" align="right">Revenue (costed)</TableCell>
                                    <TableCell data-help="accounting.buyerProfit.col.cost" align="right">Cost</TableCell><TableCell data-help="accounting.buyerProfit.col.margin" align="right">Margin</TableCell><TableCell data-help="accounting.buyerProfit.col.marginPct" align="right">Margin %</TableCell>
                                    <TableCell data-help="accounting.buyerProfit.col.uncosted" align="right">Invoiced, not costed</TableCell>
                                    {showPlan && <TableCell data-help="accounting.buyerProfit.col.planMargin" align="right">Plan margin %</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.rows.map(r => (
                                    <TableRow key={r.buyerId ?? r.buyerName} hover>
                                        <TableCell>{r.buyerName}</TableCell>
                                        <TableCell align="right">{r.orderCount} ({r.costedOrderCount})</TableCell>
                                        <TableCell align="right">{formatMoney(r.revenue)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.cost)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.margin)}</TableCell>
                                        <TableCell align="right">{r.costedOrderCount === 0 ? 'Cost incomplete' : pct(r.marginPct)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.uncostedRevenue ?? 0)}</TableCell>
                                        {showPlan && <TableCell align="right">{pct(r.planMarginPct)}</TableCell>}
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell><TableCell />
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(data.totalRevenue)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(data.totalCost)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(data.totalMargin)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{pct(data.totalMarginPct)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(data.rows.reduce((t, r) => t + (r.uncostedRevenue ?? 0), 0))}</TableCell>
                                    {showPlan && <TableCell />}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
        </Box>
    );
};

export default BuyerProfitabilityPage;
